import React, { useRef, useState } from 'react';
import { Upload, CheckCircle, Loader2, Image, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { cn } from '@/lib/utils';

interface DropsUploadAreaProps {
  rarity: 'common' | 'uncommon' | 'rare' | 'legendary';
  count: number;
  onFileSelect: (file: File) => void;
  isUploaded?: boolean;
  isUploading?: boolean;
  uploadProgress?: number;
  disabled?: boolean;
  disabledMessage?: string;
}

const rarityConfig = {
  common: {
    label: 'Common',
    bgGradient: 'from-muted/20 to-muted/10',
    borderColor: 'border-muted',
    accentColor: 'text-muted-foreground',
    hoverBg: 'hover:bg-muted/30',
  },
  uncommon: {
    label: 'Uncommon', 
    bgGradient: 'from-secondary/20 to-secondary/10',
    borderColor: 'border-secondary/30',
    accentColor: 'text-secondary',
    hoverBg: 'hover:bg-secondary/20',
  },
  rare: {
    label: 'Rare',
    bgGradient: 'from-accent/20 to-accent/10', 
    borderColor: 'border-accent/30',
    accentColor: 'text-accent',
    hoverBg: 'hover:bg-accent/20',
  },
  legendary: {
    label: 'Legendary',
    bgGradient: 'from-primary/20 to-primary/10',
    borderColor: 'border-primary/30', 
    accentColor: 'text-primary',
    hoverBg: 'hover:bg-primary/20',
  },
};

export const DropsUploadArea: React.FC<DropsUploadAreaProps> = ({
  rarity,
  count,
  onFileSelect,
  isUploaded = false,
  isUploading = false,
  uploadProgress = 0,
  disabled = false,
  disabledMessage,
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const config = rarityConfig[rarity];

  const handleClick = () => {
    if (!disabled && !isUploading) {
      fileInputRef.current?.click();
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      onFileSelect(file);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
    
    if (disabled || isUploading) return;
    
    const file = e.dataTransfer.files?.[0];
    if (file && file.type.startsWith('image/')) {
      onFileSelect(file);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    if (!disabled && !isUploading) {
      setIsDragOver(true);
    }
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
  };

  return (
    <div className={cn(
      "relative border-2 border-dashed rounded-lg p-3 transition-all duration-200",
      "bg-gradient-to-br",
      config.bgGradient,
      config.borderColor,
      !disabled && !isUploading && config.hoverBg,
      !disabled && !isUploading && "cursor-pointer",
      isDragOver && !disabled && "border-solid scale-[1.01]",
      disabled && "opacity-50 cursor-not-allowed",
      isUploaded && "border-green-500/50 bg-green-500/10"
    )}
      onClick={handleClick}
      onDrop={handleDrop}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
    >
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={handleFileChange}
        className="hidden"
        disabled={disabled || isUploading}
      />
      
      <div className="flex items-center justify-between gap-3">
        {/* Left side - Rarity info */}
        <div className="flex items-center gap-2 min-w-0">
          <h3 className={cn("text-sm font-medium capitalize", config.accentColor)}>
            {config.label}
          </h3>
          <span className="text-xs bg-background/80 px-1.5 py-0.5 rounded-full border whitespace-nowrap">
            {count} items
          </span>
        </div>

        {/* Right side - Status/Action */}
        <div className="flex items-center gap-2 flex-shrink-0">
          {disabled ? (
            <div className="flex items-center gap-1 text-muted-foreground">
              <AlertCircle className="h-4 w-4" />
              <span className="text-xs">{disabledMessage}</span>
            </div>
          ) : isUploading ? (
            <div className="flex items-center gap-2">
              <Loader2 className="h-4 w-4 animate-spin text-primary" />
              <div className="text-xs text-muted-foreground">
                {uploadProgress}%
              </div>
            </div>
          ) : isUploaded ? (
            <div className="flex items-center gap-1 text-green-600">
              <CheckCircle className="h-4 w-4" />
              <span className="text-xs font-medium">Uploaded</span>
            </div>
          ) : (
            <div className="flex items-center gap-1 text-muted-foreground">
              {isDragOver ? (
                <Upload className="h-4 w-4" />
              ) : (
                <Image className="h-4 w-4" />
              )}
              <span className="text-xs">
                {isDragOver ? 'Drop here' : 'Upload image'}
              </span>
            </div>
          )}
        </div>
      </div>

      {/* Progress bar for uploading (below main content) */}
      {isUploading && (
        <div className="mt-2">
          <Progress value={uploadProgress} className="w-full h-1" />
        </div>
      )}
    </div>
  );
};